import type { Tier } from '../types'

// Solid fill colors for the stacked bar segments. Picked one shade darker than
// the badge backgrounds so the strip reads as a chart, not a row of badges.
// T1 emerald (top) → T4 red (bottom), matching TierBadge / LeaderboardPage.
const TIER_BAR_CLASS: Record<Tier, string> = {
  1: 'bg-emerald-500',
  2: 'bg-sky-500',
  3: 'bg-amber-500',
  4: 'bg-red-500',
}

const TIER_TEXT_CLASS: Record<Tier, string> = {
  1: 'text-emerald-300',
  2: 'text-sky-300',
  3: 'text-amber-300',
  4: 'text-red-300',
}

export interface TierDistributionProps {
  counts: Record<Tier, number>
  className?: string
}

export function TierDistribution({ counts, className }: TierDistributionProps) {
  const tiers: Tier[] = [1, 2, 3, 4]
  const total = tiers.reduce((sum, t) => sum + (counts[t] || 0), 0)

  return (
    <div
      className={`rounded-lg border border-slate-800 bg-slate-900/40 p-3${className ? ` ${className}` : ''}`}
    >
      <div className="flex items-baseline justify-between">
        <p className="text-[10px] uppercase tracking-wide text-slate-500">
          Tier distribution
        </p>
        <p className="text-xs text-slate-500">
          {total} agent{total === 1 ? '' : 's'}
        </p>
      </div>

      {total === 0 ? (
        <p className="mt-2 text-xs text-slate-600">No tiered agents yet.</p>
      ) : (
        <>
          <div className="mt-2 flex h-3 w-full overflow-hidden rounded bg-slate-800">
            {tiers.map((t) => {
              const n = counts[t] || 0
              if (n === 0) return null
              const pct = (n / total) * 100
              return (
                <div
                  key={t}
                  className={TIER_BAR_CLASS[t]}
                  style={{ width: `${pct}%` }}
                  title={`T${t}: ${n} agent${n === 1 ? '' : 's'} (${pct.toFixed(1)}%)`}
                />
              )
            })}
          </div>

          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
            {tiers.map((t) => {
              const n = counts[t] || 0
              return (
                <span key={t} className="inline-flex items-center gap-1.5">
                  <span
                    className={`inline-block h-2 w-2 rounded-sm ${TIER_BAR_CLASS[t]}`}
                  />
                  <span className={`font-mono ${TIER_TEXT_CLASS[t]}`}>T{t}</span>
                  <span className="text-slate-400">{n}</span>
                </span>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
